import clsx from 'clsx';

// Hooks
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';

import type { PostType } from '@/types/postType';
// Types
import type { SocialProfileType } from '@/types/socialProfileType';

import { DefaultAvatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
// Components
import { PostComponent } from '@/components/base/Post/PostComponent';

type KeyNumberMostProps = {
    title: string;
    sp: SocialProfileType;
    post: PostType & { social_profile_id: SocialProfileType['id'] };
    icon: string;
    value: number;
    bgColor: string;
    textColor: string;
    borderColor: string;
    isLoading: boolean;
};

const KeyNumberMost = ({
    title,
    sp,
    post,
    value,
    icon,
    bgColor,
    textColor,
    borderColor,
    isLoading
}: KeyNumberMostProps) => {
    const { user } = useUser({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false
    });
    const { formatNumber } = useFormatNumber(user?.settings?.numbers_format);

    if (isLoading) {
        return (
            <div
                className="relative flex h-52 w-full rounded-md bg-white p-1 dark:bg-icoDarkMode-anthracite"
                data-testid="skeleton-loading">
                <div className="h-full w-1/2 animate-pulse rounded-sm bg-gray-100 dark:bg-icoDarkMode-deepBlack"></div>
                <div className="flex w-full flex-col justify-center space-y-5 px-8 py-5">
                    <span className="h-12 w-full animate-pulse bg-gray-100 dark:bg-icoDarkMode-deepBlack"></span>
                    <span className="h-6 w-full animate-pulse bg-gray-100 dark:bg-icoDarkMode-deepBlack"></span>
                </div>
            </div>
        );
    }

    if (!post) {
        return null;
    }

    return (
        <div
            className="flex items-center rounded-md border-4 border-white bg-white @container/keyNumberMost dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
            data-testid="key-number-most">
            <div className="w-48">
                <PostComponent
                    network={sp?.social_network_type}
                    type="medias"
                    isLoading={false}
                    data={post}
                    postConfig={{
                        hasFooter: false,
                        hasHover: false
                    }}
                />
            </div>
            <div className="grid w-[calc(100%-12rem)] gap-y-5 p-7">
                <p className="font-GBold text-xl @md/keyNumberMost:text-3xl">{title}</p>
                <div className="flex items-center space-x-3">
                    {sp ? (
                        <DefaultAvatar
                            key={sp.id}
                            size="md"
                            fallback={sp.slug?.[0]}
                            url={sp.external_api_data?.avatar}
                            profileType={sp.social_network_type}
                            title={sp.slug}
                            data-testid="social-profile-avatar"
                        />
                    ) : null}
                    <div
                        className={clsx(
                            'flex w-auto items-center gap-2 rounded-full border px-2.5 py-2 text-xs',
                            borderColor,
                            bgColor,
                            textColor
                        )}>
                        <p>{typeof value !== 'number' ? '-' : formatNumber(value)}</p>
                        <Icon icon={icon} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KeyNumberMost;
